import {useMemo} from 'react';

import {apiUpdateFarm} from 'constants/Api';

import type {
  IFarmOverviewValidationState,
  IUseFarmOverviewSubStepParams,
  IUseFarmOverviewSubStepResult,
} from '../farmInformation.types';
import {
  FARM_OVERVIEW_INITIAL_VALIDATION,
  lshFarmOverviewCountWords,
} from '../farmInformation.utils';

export function lshUseFarmOverviewSubStep({
  application,
  farmName,
  farmBackground,
  farmDirections,
  farmOwnership,
  areaLeased,
  leaseEndDate,
  ownedHectares,
  farmType,
  areaUnderIrrigation,
  areaOfDryLand,
  selectedWaterSources,
  waterRights,
  fullTimeFarming,
  shortTermGoals,
  longTermGoals,
  landComposition,
  workforceNumber,
  workerTrust,
  employmentBeforeAfter,
  isSavingFarmOverview,
  setIsSavingFarmOverview,
  setFarmOverviewError,
  farmValidationErrors,
  setFarmValidationErrors,
  setSelectedWaterSources,
  setApplication,
  refreshJourney,
}: IUseFarmOverviewSubStepParams): IUseFarmOverviewSubStepResult {
  const farmBackgroundWordCount = useMemo(
    () => lshFarmOverviewCountWords(farmBackground),
    [farmBackground],
  );

  const clearFarmValidationError = (
    key: keyof IFarmOverviewValidationState,
  ) => {
    if (!farmValidationErrors[key]) return;
    setFarmValidationErrors((current) => ({
      ...current,
      [key]: false,
    }));
  };

  const handleWaterSourceToggle = (source: string, checked: boolean) => {
    setSelectedWaterSources((current) => {
      if (checked) {
        if (current.includes(source)) return current;
        return [...current, source];
      }

      return current.filter((item) => item !== source);
    });
  };

  const validateFarmOverview = (): boolean => {
    const nextErrors: IFarmOverviewValidationState = {
      ...FARM_OVERVIEW_INITIAL_VALIDATION,
    };
    let hasError = false;

    if (!farmName.trim()) {
      nextErrors.farmName = true;
      hasError = true;
    }
    if (!farmBackground.trim()) {
      nextErrors.farmBackground = true;
      hasError = true;
    }
    if (!farmDirections.trim()) {
      nextErrors.farmDirections = true;
      hasError = true;
    }
    if (!farmOwnership) {
      nextErrors.farmOwnership = true;
      hasError = true;
    }
    if (farmOwnership === 'Owned' || farmOwnership === 'Both') {
      if (!ownedHectares.trim()) {
        nextErrors.ownedHectares = true;
        hasError = true;
      }
    }
    if (farmOwnership === 'Leased' || farmOwnership === 'Both') {
      if (!areaLeased.trim()) {
        nextErrors.areaLeased = true;
        hasError = true;
      }
      if (!leaseEndDate) {
        nextErrors.leaseEndDate = true;
        hasError = true;
      }
    }
    if (!farmType) {
      nextErrors.farmType = true;
      hasError = true;
    }
    if (farmType === 'Irrigated' || farmType === 'Both') {
      if (!areaUnderIrrigation.trim()) {
        nextErrors.areaUnderIrrigation = true;
        hasError = true;
      }
    }
    if (farmType === 'Dryland' || farmType === 'Both') {
      if (!areaOfDryLand.trim()) {
        nextErrors.areaOfDryLand = true;
        hasError = true;
      }
    }
    if (!waterRights) {
      nextErrors.waterRights = true;
      hasError = true;
    }
    if (!fullTimeFarming) {
      nextErrors.fullTimeFarming = true;
      hasError = true;
    }
    if (!shortTermGoals.trim()) {
      nextErrors.shortTermGoals = true;
      hasError = true;
    }
    if (!longTermGoals.trim()) {
      nextErrors.longTermGoals = true;
      hasError = true;
    }
    if (!landComposition.trim()) {
      nextErrors.landComposition = true;
      hasError = true;
    }
    if (!workforceNumber.trim()) {
      nextErrors.workforceNumber = true;
      hasError = true;
    }
    if (!workerTrust.trim()) {
      nextErrors.workerTrust = true;
      hasError = true;
    }
    if (!employmentBeforeAfter.trim()) {
      nextErrors.employmentBeforeAfter = true;
      hasError = true;
    }

    setFarmValidationErrors(nextErrors);

    if (hasError) {
      setFarmOverviewError(
        'Please complete all required farm overview fields.',
      );
      return false;
    }

    if (farmBackgroundWordCount < 50) {
      setFarmOverviewError(
        `Farm background must have at least 50 words. Current word count: ${farmBackgroundWordCount}/50.`,
      );
      setFarmValidationErrors((current) => ({
        ...current,
        farmBackground: true,
      }));
      return false;
    }

    setFarmOverviewError(null);
    return true;
  };

  const handleSaveFarmOverview = async (): Promise<boolean> => {
    if (isSavingFarmOverview) return false;

    if (!application?.farm?.id) {
      setFarmOverviewError(
        'Farm record is unavailable. Please refresh and try again.',
      );
      return false;
    }

    if (!validateFarmOverview()) {
      return false;
    }

    const payload: any = {
      farmId: Number(application.farm.id),
      name: farmName.trim(),
      description: farmBackground.trim(),
      directions: farmDirections.trim(),
      farmArrangement: farmOwnership.toUpperCase(),
      numberOfLeasedHectares: areaLeased.trim() ? Number(areaLeased) : null,
      isLeased: farmOwnership === 'Leased' || farmOwnership === 'Both',
      irrigatedHectares: areaUnderIrrigation.trim()
        ? Number(areaUnderIrrigation)
        : null,
      drylandHectares: areaOfDryLand.trim() ? Number(areaOfDryLand) : null,
      ownedHectares: ownedHectares.trim() ? Number(ownedHectares) : null,
      leaseEndDate: leaseEndDate ? Number(leaseEndDate) : null,
      borehole: selectedWaterSources.includes('Borehole'),
      river: selectedWaterSources.includes('River'),
      dam: selectedWaterSources.includes('Dam'),
      waterRights: waterRights === 'Yes',
      fullTimeFarming: fullTimeFarming === 'Yes',
      shortTermGoals: shortTermGoals.trim(),
      longTermGoals: longTermGoals.trim(),
      landComposition: landComposition.trim(),
      workforceNumber: workforceNumber.trim() ? Number(workforceNumber) : null,
      workerTrust: workerTrust.trim(),
      employmentBeforeAndAfter: employmentBeforeAfter.trim(),
    };

    try {
      setIsSavingFarmOverview(true);
      await apiUpdateFarm(payload);

      setApplication((current: any) => {
        if (!current || !current.farm) return current;

        return {
          ...current,
          farm: {
            ...current.farm,
            ...payload,
            id: current.farm.id,
            address: payload.directions || current.farm.address,
          },
        };
      });

      await refreshJourney();
      setFarmOverviewError(null);
      return true;
    } catch (error) {
      console.error(
        '[ApplicationFarmInformationStep] Failed to save farm overview',
        error,
      );
      setFarmOverviewError(
        'Could not save farm overview details. Please try again.',
      );
      return false;
    } finally {
      setIsSavingFarmOverview(false);
    }
  };

  return {
    farmBackgroundWordCount,
    clearFarmValidationError,
    handleWaterSourceToggle,
    handleSaveFarmOverview,
  };
}
